import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';

const DEMO_URL = 'https://demo.bookmein.events/';

const Demo = () => {
  const { t } = useLanguage();
  const [device, setDevice] = useState('desktop');

  return (
    <section id="demo" className="py-24 bg-slate-900 border-y border-slate-800 font-sans relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>

      {/* Header text */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">
            {t('demo.title')}
          </h2>
          <p className="text-slate-500 max-w-2xl mx-auto mb-12 text-sm leading-relaxed">
            {t('demo.subdesc')}
          </p>
        </motion.div>
      </div>

      {/* Wider container for the mockup */}
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">

        {/* Premium Browser Mockup Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="w-full rounded-3xl bg-slate-950/40 border border-slate-800/80 shadow-2xl overflow-hidden flex flex-col p-2 backdrop-blur-xl"
        >
          {/* Browser Header Bar */}
          <div className="flex items-center gap-4 px-4 py-3 bg-slate-950/80 border-b border-slate-900 rounded-t-2xl">
            {/* Window control dots */}
            <div className="flex gap-1.5 shrink-0">
              <div className="w-3 h-3 rounded-full bg-rose-500/80"></div>
              <div className="w-3 h-3 rounded-full bg-amber-500/80"></div>
              <div className="w-3 h-3 rounded-full bg-emerald-500/80"></div>
            </div>

            {/* Address Bar */}
            <div className="flex-1 max-w-lg mx-auto bg-slate-900/60 rounded-lg px-4 py-1.5 border border-slate-800/40 flex items-center justify-between text-left">
              <span className="text-[10px] text-slate-500 font-mono select-none overflow-hidden text-ellipsis whitespace-nowrap">
                {DEMO_URL}
              </span>
              <SafeIcon name="Lock" className="w-3 h-3 text-emerald-500 shrink-0" />
            </div>

            {/* Desktop / Mobile toggle */}
            <div className="flex gap-1 shrink-0 bg-slate-900/60 rounded-lg p-1 border border-slate-800/40">
              <button
                onClick={() => setDevice('desktop')}
                aria-label="Desktop view"
                className={`p-1.5 rounded-md transition-colors ${
                  device === 'desktop'
                    ? 'bg-indigo-600 text-white shadow'
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                <SafeIcon name="Monitor" className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setDevice('mobile')}
                aria-label="Mobile view"
                className={`p-1.5 rounded-md transition-colors ${
                  device === 'mobile'
                    ? 'bg-indigo-600 text-white shadow'
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                <SafeIcon name="Smartphone" className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Browser Viewport (live sandbox) */}
          {device === 'desktop' ? (
            <div className="relative bg-slate-950 aspect-[16/9] w-full overflow-hidden rounded-b-2xl">
              <iframe
                src={DEMO_URL}
                title="BookMeIn.Events interactive demo"
                className="w-full h-full border-0"
                allow="clipboard-write"
              ></iframe>
            </div>
          ) : (
            <div className="relative bg-slate-900/60 w-full overflow-hidden rounded-b-2xl py-8 flex items-center justify-center">
              {/* Phone frame */}
              <div className="w-[360px] max-w-full aspect-[9/19] rounded-[2.5rem] border-4 border-slate-800 bg-slate-950 overflow-hidden shadow-2xl relative">
                {/* Speaker notch */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 bg-slate-800 rounded-full z-10"></div>
                <iframe
                  src={DEMO_URL}
                  title="BookMeIn.Events interactive demo (mobile)"
                  className="w-full h-full border-0"
                  allow="clipboard-write"
                ></iframe>
              </div>
            </div>
          )}
        </motion.div>

        {/* Open full demo link */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-8 flex justify-center"
        >
          <a
            href={DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white hover:bg-slate-100 text-slate-900 px-6 py-3 rounded-xl text-sm font-bold shadow-md inline-flex items-center gap-2 transition-colors"
          >
            {t('demo.start')} <SafeIcon name="ExternalLink" className="w-4 h-4" />
          </a>
        </motion.div>

      </div>
    </section>
  );
};

export default Demo;
